import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import i18n from 'src/utils/translate';
import FindCommunityCard from './findCommunityCard';
import FindCommunitySearchBar from './findCommunitySearchBar';
import styled from 'styled-components';
import api from 'src/utils/api';
import { error } from 'loglevel';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import Loader from 'src/components/loader';

const FindCommunityScreen = ({
  suggestedCommunities,
  setJoinedCommunities,
  showJoinedCommunitiesValidationError,
  setJoinedCommunitiesError,
}) => {
  const [recommendedCommunities, setRecommendedCommunities] = useState(suggestedCommunities);
  const [noResultsQuery, setNoResultsQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'context' does not exist on type 'Default... Remove this comment to see the full error message
  const currentUser = useSelector(state => state.context.currentUser);

  useEffect(() => {
    if (!!isLoading && !!currentUser && !!currentUser.followedCommunities.length) {
      setIsLoading(false);
    }
    if (currentUser) {
      const validCommunities = currentUser.followedCommunities.filter(c => c.slug !== 'blog');
      if (validCommunities.length !== 0) {
        setJoinedCommunities(true);
        setJoinedCommunitiesError(false);
      } else {
        setJoinedCommunities(false);
      }
    }
  }, [currentUser]);

  async function fetchCommunities() {
    const response = await api.getPublic('communities/related')
      .catch((e) => error(e));

    if (response && response?.data?.length > 0) {
      setRecommendedCommunities(response.data.map(community => ({
        ...community,
        communityId: community.groupId,
        slug: community.groupCode,
        followerCount: community.membersInCommunity,
      })));
    }
  }

  useEffect(() => {
    if (suggestedCommunities.length === 0) {
      fetchCommunities();
    }
  }, []);

  const onSearch = async (query) => {
    setNoResultsQuery(null);

    const response = await api.getPublic('search/communities', { params: { q: query } })
      .catch((e) => error(e));

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'data' does not exist on type 'void | Axi... Remove this comment to see the full error message
    const { data: { communities } } = response;

    const footfallQuery = communities.length === 0 ? `no-results:${query}` : query;
    trackFootfallEvent(footfallEventTypes.SEARCHED_SITE, { searchQuery: footfallQuery, searchType: 'communities-join' });

    if (!communities.length) setNoResultsQuery(query);
    setRecommendedCommunities(communities.map(c => {
      return {
        ...c,
        followerCount: c.totalMembers,
      };
    }));

    setJoinedCommunitiesError(false);
  };

  return (
    <div>
      <Label margin={'10px'}>{i18n.t('Your communities')}</Label>
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <div data-test-id="signup-followed-communities">
          {!!currentUser && currentUser.followedCommunities.map(community =>
            <FindCommunityCard
              community={community}
              following={true}
              key={community.communityId}
            />,
          )}
        </div>
      )}
      <SearchLabelWrapper>
        <Label margin={'5px'}>{i18n.t('Search')}</Label>
        <SearchLabelHint>{i18n.t('Type conditions, treatments, interests')}</SearchLabelHint>
      </SearchLabelWrapper>
      <SearchBarWrapper>
        <FindCommunitySearchBar
          error={showJoinedCommunitiesValidationError ? 'Enjoy HealthUnlocked by following at least one health community' : null}
          placeholderText={i18n.t('e.g. cancer, running, pain')}
          onSearch={onSearch}
        />
      </SearchBarWrapper>
      {!!noResultsQuery &&
        <EmptyState data-test-id="signup-find-communities-empty-state">
          <EmptyStateText>{i18n.t('0 public communities')}</EmptyStateText>
          <EmptyStateText>{i18n.t('Sorry, we don\'t have any public communities matching ') + noResultsQuery}</EmptyStateText>
          <EmptyStateText>{i18n.t('Try broadening your search results to get more results')}</EmptyStateText>
        </EmptyState>
      }
      <SuggestedCommunitiesWrapper data-test-id="signup-suggested-communities">
        {recommendedCommunities.map(community => (
          <FindCommunityCard
            community={community}
            following={false}
            hidden={!!currentUser && !!currentUser.followedCommunities &&
              !!currentUser.followedCommunities.find(c => c.communityId === community.communityId)}
            key={community.communityId}
          />
        ))}
      </SuggestedCommunitiesWrapper>
    </div>
  );
};

FindCommunityScreen.propTypes = {
  suggestedCommunities: PropTypes.array,
};

FindCommunityScreen.defaultProps = {
  suggestedCommunities: [],
};

const LoaderWrapper = styled.div`
  height: 45px;

  .hu-loader {
    height: 5px;
  }
`;

const SearchBarWrapper = styled.div`
  margin: 15px 0;

  .search-bar {
    margin-bottom: 0;
  }
`;

const EmptyState = styled.div`
  margin-top: 25px;
`;

const SearchLabelWrapper = styled.div`
  margin-top: 10px;
`;

const Label = styled.p<{margin: string}>`
  margin-bottom: ${({margin}) => `${margin} !important`};
  font-weight: bold;
`;

const SearchLabelHint = styled.p`
  color: ${props => props.theme.colorTextLight};
`;

const EmptyStateText = styled.div`
  margin-bottom: 10px;
`;

const SuggestedCommunitiesWrapper = styled.div`
  max-height: 40vh;
`;

export default FindCommunityScreen;
